import { CANVAS_CUSTOM_PROPERTIES_FIELD, FONT_FAMILIES_LIST } from "../constants/canvasConstants";
import type { ActiveSelection, IEvent, Object as FabricObject } from "fabric/fabric-impl";
import { ExtendedObject } from "./objectTypes";

export type Canvas = fabric.Canvas & {
  [CANVAS_CUSTOM_PROPERTIES_FIELD]?: { isHistoryProcessing: boolean };
};
export type Object = FabricObject;
export type Textbox = fabric.Textbox;
export type FabricData = ReturnType<Canvas["toJSON"]>;
export type BackgroundImageFillMode = "cover" | "contain";

export type Extension = {
  name: string;
  onInit?: (canvas: Canvas) => void;
  onDestroy?: (canvas: Canvas) => void;
};

export type ObjectExtension<AddObjectPayload = unknown, ModifyObjectPayload = unknown> = Extension & {
  onAddObject: (data: { canvas: Canvas; payload: AddObjectPayload }) => void;
  onModifyObject: (data: { canvas: Canvas; payload: ModifyObjectPayload }) => void;
};
export type BasicExtensionFactory<Options = void> = (options: Options) => Extension;
export type ObjectExtensionFactory<AddObjectPayload, ModifyObjectPayload, Options = any> = (
  options: Options
) => ObjectExtension<AddObjectPayload, ModifyObjectPayload>;

export type FabricExtension = Extension | ObjectExtension;

export type ManageObjectData<T = any> = { name: string; payload: T };
export type ManageObjectDataFactory<T> = (payload: T) => ManageObjectData<T>;
export type FontFamily = (typeof FONT_FAMILIES_LIST)[number];
export type ObjectSelectionHandler = (data: { e: IEvent<MouseEvent>; selected: Object[] }) => void;
export type ObjectDeselectionHandler = (data: { e: IEvent<MouseEvent>; deselected: Object[] }) => void;

// Type guards
export const isObjectExtension = (extension: FabricExtension): extension is ObjectExtension => {
  return (
    (extension as ObjectExtension).onAddObject !== undefined ||
    (extension as ObjectExtension).onModifyObject !== undefined
  );
};

export const isActiveSelection = (object: Object): object is ActiveSelection => {
  return object.type === "activeSelection";
};

export type AspectRatio = "16:9" | "9:16" | "4:3" | "3:4" | "1:1";

export type CanvasData = { version: string; objects: any[] };

export type CanvasDataChange =
  | {
      type: "add-or-replace";
      object: NonNullable<CanvasData>["objects"][number];
      index: number;
    }
  | {
      type: "delete";
      meaning: NonNullable<ExtendedObject["meaning"]>;
    };
